import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, Plus, X, Search, Save, ChevronDown, ChevronUp, Lock, FileDown, Send } from 'lucide-react';
import api, { methodsApi, sessionsApi, noteTemplatesApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface SelectedMethod {
  method_id: string;
  method_name: string;
  method_description?: string;
  notizen: string;
}

interface SessionData {
  checkin_befinden: string;
  checkin_skala: number;
  seit_letzter_sitzung: string;
  hausaufgaben_erledigt: string;
  thema: string;
  methoden: SelectedMethod[];
  durchfuehrung: string;
  ergebnisse: string;
  vereinbarungen: string;
  hausaufgaben: string;
  checkout_befinden: string;
  checkout_skala: number;
  hausaufgaben_naechste: string;
  naechster_termin: string;
  // Vorbereitung (coach-intern)
  vorb_intention: string;
  vorb_methoden: string;
  vorb_offene_punkte: string;
}

const defaultData: SessionData = {
  checkin_befinden: '',
  checkin_skala: 5,
  seit_letzter_sitzung: '',
  hausaufgaben_erledigt: '',
  thema: '',
  methoden: [],
  durchfuehrung: '',
  ergebnisse: '',
  vereinbarungen: '',
  hausaufgaben: '',
  checkout_befinden: '',
  checkout_skala: 5,
  hausaufgaben_naechste: '',
  naechster_termin: '',
  vorb_intention: '',
  vorb_methoden: '',
  vorb_offene_punkte: '',
};

const sessionStatusLabels: Record<string, string> = {
  scheduled: 'Geplant',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  cancelled: 'Abgesagt',
  no_show: 'Nicht erschienen',
  postponed: 'Verschoben',
};

const sessionStatusBadge: Record<string, string> = {
  scheduled: 'badge-new',
  in_progress: 'badge-ongoing',
  completed: 'badge-active',
  cancelled: 'badge-completed',
  no_show: 'badge-paused',
};

interface Props {
  data: any;
  onSave: (data: any) => Promise<void>;
  blockId: string;
  clientEmail: string;
  // Session-Metadaten:
  sessionId?: string;
  sessionNumber?: number;
  sessionDate?: string;
  sessionDuration?: number;
  sessionFormat?: string;
  sessionStatus?: string;
  onSessionUpdate?: (data: any) => Promise<void>;
  // Vorbereitung: Notizen aus vorheriger Sitzung
  previousSessionNotes?: string;
}

// Hilfsfunktion: ISO-Datum in datetime-local String umwandeln
function toDatetimeLocal(isoString: string | undefined): string {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

export function SessionForm({ data, onSave, blockId, clientEmail, sessionId, sessionNumber, sessionDate, sessionDuration, sessionFormat, sessionStatus, onSessionUpdate, previousSessionNotes }: Props) {
  const { showToast } = useToast();
  const [form, setForm] = useState<SessionData>({ ...defaultData, ...data });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Vorbereitung accordion: offen wenn bereits Inhalt vorhanden
  const hasVorbContent = !!(form.vorb_intention || form.vorb_methoden || form.vorb_offene_punkte);
  const [vorbOpen, setVorbOpen] = useState(hasVorbContent);
  // Session header form
  const [headerForm, setHeaderForm] = useState({
    scheduled_at: toDatetimeLocal(sessionDate),
    duration_minutes: sessionDuration?.toString() || '90',
    location_type: sessionFormat || 'in_person',
    status: sessionStatus || 'scheduled',
  });
  // Auto-Save draft (debounced)
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>('');

  const autoSaveDraft = useCallback(async (formData: SessionData) => {
    if (!sessionId) return;
    const snapshot = JSON.stringify(formData);
    if (snapshot === lastSavedRef.current) return;
    setAutoSaveStatus('saving');
    try {
      await sessionsApi.saveDraft(sessionId, formData);
      lastSavedRef.current = snapshot;
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch {
      setAutoSaveStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => autoSaveDraft(form), 3000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [form, sessionId, autoSaveDraft]);

  // Load saved draft on mount
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const res = await sessionsApi.getDraft(sessionId);
        if (res.data.notes_draft && Object.keys(res.data.notes_draft).length > 0) {
          const merged = { ...defaultData, ...data, ...res.data.notes_draft };
          setForm(merged);
          lastSavedRef.current = JSON.stringify(merged);
        }
      } catch { /* ignore — no saved draft */ }
    })();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note templates
  const [noteTemplates, setNoteTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const res = await noteTemplatesApi.list();
        const templates = res.data || [];
        setNoteTemplates(templates);
      } catch { /* ignore — templates not available */ }
    })();
  }, []);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const tmpl = noteTemplates.find((t: any) => t.id === templateId);
    if (!tmpl) return;
    // Template structure contains sections with enabled flags
    // We don't modify the form data, just visually hide disabled sections
    // This is handled by the visibleSections set below
  };

  // Determine which sections are visible based on selected template
  const visibleSections = React.useMemo(() => {
    if (!selectedTemplateId) return null; // null = show all
    const tmpl = noteTemplates.find((t: any) => t.id === selectedTemplateId);
    if (!tmpl?.structure?.sections) return null;
    const enabled = new Set<string>();
    for (const s of tmpl.structure.sections) {
      if (s.enabled) enabled.add(s.key);
    }
    return enabled;
  }, [selectedTemplateId, noteTemplates]);

  const isSectionVisible = (key: string) => visibleSections === null || visibleSections.has(key);

  // Method search
  const [showMethodSearch, setShowMethodSearch] = useState(false);
  const [methodQuery, setMethodQuery] = useState('');
  const [methodResults, setMethodResults] = useState<any[]>([]);
  const [methodLoading, setMethodLoading] = useState(false);
  const methodSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update header form when props change
  useEffect(() => {
    setHeaderForm({
      scheduled_at: toDatetimeLocal(sessionDate),
      duration_minutes: sessionDuration?.toString() || '90',
      location_type: sessionFormat || 'in_person',
      status: sessionStatus || 'scheduled',
    });
  }, [sessionDate, sessionDuration, sessionFormat, sessionStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      // Session-Metadaten speichern
      if (onSessionUpdate) {
        await onSessionUpdate({
          scheduled_at: headerForm.scheduled_at ? new Date(headerForm.scheduled_at).toISOString() : undefined,
          duration_minutes: parseInt(headerForm.duration_minutes) || undefined,
          location_type: headerForm.location_type,
          status: headerForm.status,
          content_data: form,
        });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  // Method search effect
  useEffect(() => {
    if (!methodQuery.trim()) {
      setMethodResults([]);
      return;
    }
    if (methodSearchTimer.current) clearTimeout(methodSearchTimer.current);
    methodSearchTimer.current = setTimeout(async () => {
      setMethodLoading(true);
      try {
        const res = await methodsApi.list({ search: methodQuery });
        setMethodResults(res.data.items || []);
      } catch {
        setMethodResults([]);
      }
      setMethodLoading(false);
    }, 400);
    return () => { if (methodSearchTimer.current) clearTimeout(methodSearchTimer.current); };
  }, [methodQuery]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'range') {
      setForm({ ...form, [name]: Number(value) });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // Character count helper
  const charCount = (text: string) => text?.length || 0;

  const addMethod = (method: any) => {
    if (form.methoden.some((m) => m.method_id === method.id)) return;
    setForm({
      ...form,
      methoden: [
        ...form.methoden,
        {
          method_id: method.id,
          method_name: method.name,
          method_description: method.description || '',
          notizen: '',
        },
      ],
    });
    setShowMethodSearch(false);
    setMethodQuery('');
    setMethodResults([]);
  };

  const updateMethodNotes = (index: number, notizen: string) => {
    const updated = [...form.methoden];
    updated[index] = { ...updated[index], notizen };
    setForm({ ...form, methoden: updated });
  };

  const removeMethod = (index: number) => {
    setForm({ ...form, methoden: form.methoden.filter((_, i) => i !== index) });
  };

  const handleExport = async () => {
    if (sessionId) {
      // Use dedicated notes PDF endpoint
      try {
        const res = await sessionsApi.exportNotesPdf(sessionId);
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Sitzungsnotizen_${sessionNumber || ''}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('PDF heruntergeladen', 'success');
      } catch {
        showToast('PDF-Export fehlgeschlagen', 'error');
      }
    } else {
      // Fallback: block HTML export
      try {
        const res = await api.get(`/blocks/${blockId}/export/html`, { responseType: 'text' });
        const win = window.open('', '_blank');
        if (win) {
          win.document.write(res.data);
          win.document.close();
        }
      } catch {
        showToast('Export fehlgeschlagen', 'error');
      }
    }
  };

  return (
    <div className="block-form">
      {/* Sitzungs-Header */}
      {sessionId && (
        <div className="block-form-section" style={{ background: 'var(--bg)', borderRadius: 'var(--radius)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Sitzung {sessionNumber}</h3>
            <span className={`badge ${sessionStatusBadge[headerForm.status] || 'badge-new'}`}>
              {sessionStatusLabels[headerForm.status] || headerForm.status}
            </span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Datum & Uhrzeit</label>
              <input
                type="datetime-local"
                value={headerForm.scheduled_at}
                onChange={(e) => setHeaderForm({ ...headerForm, scheduled_at: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Dauer (Min.)</label>
              <input
                type="number"
                value={headerForm.duration_minutes}
                onChange={(e) => setHeaderForm({ ...headerForm, duration_minutes: e.target.value })}
                min={15}
                step={15}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Format</label>
              <select
                value={headerForm.location_type}
                onChange={(e) => setHeaderForm({ ...headerForm, location_type: e.target.value })}
              >
                <option value="in_person">Präsenz</option>
                <option value="online">Online</option>
                <option value="phone">Telefon</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={headerForm.status}
                onChange={(e) => setHeaderForm({ ...headerForm, status: e.target.value })}
              >
                <option value="scheduled">Geplant</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="completed">Abgeschlossen</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Vorlage wählen */}
      {noteTemplates.length > 0 && (
        <div className="block-form-section" style={{ padding: '12px 16px', background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ margin: 0, fontSize: '0.85rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Vorlage:</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              style={{ flex: 1, maxWidth: 300 }}
            >
              <option value="">Alle Sektionen anzeigen</option>
              {noteTemplates.map((t: any) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.is_default ? ' (Standard)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Vorbereitung (Accordion, coach-intern) */}
      {(isSectionVisible('vorb_intention') || isSectionVisible('vorb_methoden') || isSectionVisible('vorb_offene_punkte')) && (
      <div className="block-form-section" style={{ overflow: 'hidden' }}>
        <div
          onClick={() => setVorbOpen(!vorbOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {vorbOpen ? <ChevronUp size={16} color="var(--primary)" /> : <ChevronDown size={16} color="var(--primary)" />}
          <h4 style={{ margin: 0 }}>Vorbereitung</h4>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>(optional)</span>
          <span
            title="Nur für dich sichtbar — wird nicht an Klienten gesendet"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              background: 'var(--bg)',
              padding: '2px 8px',
              borderRadius: 10,
              marginLeft: 'auto',
              cursor: 'help',
            }}
          >
            <Lock size={10} /> Nur für dich
          </span>
        </div>
        <div style={{
          maxHeight: vorbOpen ? 600 : 0,
          opacity: vorbOpen ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease, opacity 0.2s ease',
          marginTop: vorbOpen ? 12 : 0,
        }}>
          {/* Letzte Sitzung - Erkenntnisse (read-only) */}
          {previousSessionNotes && sessionNumber && sessionNumber > 1 && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Letzte Sitzung — Erkenntnisse
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(automatisch)</span>
              </label>
              <div style={{
                background: 'var(--bg)',
                borderRadius: 'var(--radius-sm)',
                padding: '10px 14px',
                fontSize: '0.88rem',
                color: 'var(--text-light)',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                borderLeft: '3px solid var(--border)',
              }}>
                {previousSessionNotes}
              </div>
            </div>
          )}
          <div className="form-group">
            <label>Meine Intention für diese Sitzung</label>
            <textarea
              name="vorb_intention"
              value={form.vorb_intention}
              onChange={handleChange}
              placeholder="Was möchte ich heute erreichen?"
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Geplante Methoden / Übungen</label>
            <textarea
              name="vorb_methoden"
              value={form.vorb_methoden}
              onChange={handleChange}
              placeholder="Welche Methoden oder Übungen plane ich einzusetzen?"
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Offene Punkte aus letzter Sitzung</label>
            <textarea
              name="vorb_offene_punkte"
              value={form.vorb_offene_punkte}
              onChange={handleChange}
              placeholder="Gibt es etwas, das aus der letzten Sitzung noch offen ist?"
              rows={2}
            />
          </div>
        </div>
      </div>
      )}

      {/* Check-in */}
      {(isSectionVisible('checkin_befinden') || isSectionVisible('checkin_skala') || isSectionVisible('seit_letzter_sitzung') || isSectionVisible('hausaufgaben_erledigt')) && (
      <div className="block-form-section">
        <h4>Check-in</h4>
        {isSectionVisible('checkin_befinden') && (
        <div className="form-group">
          <label>Wie geht es dem Klienten?</label>
          <textarea name="checkin_befinden" value={form.checkin_befinden} onChange={handleChange} placeholder="Aktuelles Befinden..." />
        </div>
        )}
        {isSectionVisible('checkin_skala') && (
        <div className="form-group">
          <label>Befinden auf einer Skala 1-10</label>
          <div className="range-display">
            <input type="range" name="checkin_skala" min={1} max={10} value={form.checkin_skala} onChange={handleChange} style={{ flex: 1 }} />
            <span className="range-value">{form.checkin_skala}</span>
          </div>
        </div>
        )}
        {isSectionVisible('seit_letzter_sitzung') && (
        <div className="form-group">
          <label>Was ist seit der letzten Sitzung passiert?</label>
          <textarea name="seit_letzter_sitzung" value={form.seit_letzter_sitzung} onChange={handleChange} placeholder="Entwicklungen seit der letzten Sitzung..." />
        </div>
        )}
        {isSectionVisible('hausaufgaben_erledigt') && (
        <div className="form-group">
          <label>Hausaufgaben aus letzter Sitzung erledigt?</label>
          <select name="hausaufgaben_erledigt" value={form.hausaufgaben_erledigt} onChange={handleChange}>
            <option value="">-- Bitte wählen --</option>
            <option value="ja">Ja</option>
            <option value="teilweise">Teilweise</option>
            <option value="nein">Nein</option>
            <option value="keine">Keine</option>
          </select>
        </div>
        )}
      </div>
      )}

      {/* Thema */}
      {isSectionVisible('thema') && (
      <div className="block-form-section">
        <h4>Thema der Sitzung</h4>
        <div className="form-group">
          <label>Was ist das heutige Thema?</label>
          <textarea name="thema" value={form.thema} onChange={handleChange} placeholder="Heutiges Kernthema..." />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>{charCount(form.thema)} Zeichen</div>
        </div>
      </div>
      )}

      {/* Methoden */}
      {isSectionVisible('methoden') && (
      <div className="block-form-section">
        <h4>Methoden</h4>
        {form.methoden.map((m, i) => (
          <div className="method-card" key={m.method_id}>
            <div className="method-card-header">
              <span className="method-card-name">{m.method_name}</span>
              <button className="btn-icon" onClick={() => removeMethod(i)} title="Entfernen">
                <X size={14} />
              </button>
            </div>
            {m.method_description && (
              <div className="method-card-desc">
                {m.method_description.length > 150 ? m.method_description.substring(0, 150) + '...' : m.method_description}
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <input
                type="text"
                value={m.notizen}
                onChange={(e) => updateMethodNotes(i, e.target.value)}
                placeholder="Notizen zur Anwendung..."
              />
            </div>
          </div>
        ))}

        {showMethodSearch ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text"
                value={methodQuery}
                onChange={(e) => setMethodQuery(e.target.value)}
                placeholder="Methode suchen..."
                style={{ paddingLeft: 32 }}
                autoFocus
              />
            </div>
            {methodLoading && <div className="text-sm text-muted" style={{ padding: 8 }}>Suche...</div>}
            {methodResults.length > 0 && (
              <div className="method-search-results">
                {methodResults.map((m: any) => (
                  <div className="method-search-item" key={m.id} onClick={() => addMethod(m)}>
                    <div style={{ fontWeight: 500, fontSize: '0.88rem' }}>{m.name}</div>
                    {m.description && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {m.description.length > 100 ? m.description.substring(0, 100) + '...' : m.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {methodQuery && !methodLoading && methodResults.length === 0 && (
              <div className="text-sm text-muted" style={{ padding: 8 }}>Keine Methoden gefunden.</div>
            )}
            <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => { setShowMethodSearch(false); setMethodQuery(''); setMethodResults([]); }}>
              <X size={14} /> Abbrechen
            </button>
          </div>
        ) : (
          <button className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => setShowMethodSearch(true)}>
            <Plus size={14} /> Methode hinzufügen
          </button>
        )}
      </div>
      )}

      {/* Durchführung */}
      {isSectionVisible('durchfuehrung') && (
      <div className="block-form-section">
        <h4>Durchführung & Beobachtungen</h4>
        <div className="form-group">
          <label>Coach-Notizen während der Sitzung</label>
          <textarea name="durchfuehrung" value={form.durchfuehrung} onChange={handleChange} placeholder="Beobachtungen, Interventionen, Reaktionen..." style={{ minHeight: 120 }} />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>{charCount(form.durchfuehrung)} Zeichen</div>
        </div>
      </div>
      )}

      {/* Ergebnisse */}
      {isSectionVisible('ergebnisse') && (
      <div className="block-form-section">
        <h4>Ergebnisse & Erkenntnisse</h4>
        <div className="form-group">
          <label>Was wurde erreicht? Welche Erkenntnisse gab es?</label>
          <textarea name="ergebnisse" value={form.ergebnisse} onChange={handleChange} placeholder="Zentrale Ergebnisse und Erkenntnisse..." />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>{charCount(form.ergebnisse)} Zeichen</div>
        </div>
      </div>
      )}

      {/* Vereinbarungen */}
      {isSectionVisible('vereinbarungen') && (
      <div className="block-form-section">
        <h4>Vereinbarungen</h4>
        <div className="form-group">
          <label>Was wurde vereinbart?</label>
          <textarea name="vereinbarungen" value={form.vereinbarungen} onChange={handleChange} placeholder="Getroffene Vereinbarungen..." />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>{charCount(form.vereinbarungen)} Zeichen</div>
        </div>
      </div>
      )}

      {/* Hausaufgaben */}
      {isSectionVisible('hausaufgaben') && (
      <div className="block-form-section">
        <h4>Hausaufgaben</h4>
        <div className="form-group">
          <label>Aufgaben für den Klienten bis zur nächsten Sitzung</label>
          <textarea name="hausaufgaben" value={form.hausaufgaben} onChange={handleChange} placeholder="Konkrete Aufgaben und Übungen..." />
        </div>
      </div>
      )}

      {/* Check-out */}
      {(isSectionVisible('checkout_befinden') || isSectionVisible('checkout_skala')) && (
      <div className="block-form-section">
        <h4>Check-out</h4>
        {isSectionVisible('checkout_befinden') && (
        <div className="form-group">
          <label>Wie geht es dem Klienten jetzt?</label>
          <textarea name="checkout_befinden" value={form.checkout_befinden} onChange={handleChange} placeholder="Befinden nach der Sitzung..." />
        </div>
        )}
        {isSectionVisible('checkout_skala') && (
        <div className="form-group">
          <label>Befinden auf einer Skala 1-10</label>
          <div className="range-display">
            <input type="range" name="checkout_skala" min={1} max={10} value={form.checkout_skala} onChange={handleChange} style={{ flex: 1 }} />
            <span className="range-value">{form.checkout_skala}</span>
          </div>
        </div>
        )}
      </div>
      )}

      {/* Vereinbarungen für nächste Sitzung */}
      {isSectionVisible('hausaufgaben_naechste') && (
      <div className="block-form-section">
        <h4>Vereinbarungen & Hausaufgaben für nächste Sitzung</h4>
        <div className="form-group">
          <textarea name="hausaufgaben_naechste" value={form.hausaufgaben_naechste} onChange={handleChange} placeholder="Was wurde für die nächste Sitzung vereinbart..." />
        </div>
      </div>
      )}

      {/* Nächster Termin */}
      {isSectionVisible('naechster_termin') && (
      <div className="block-form-section">
        <h4>Nächster Termin</h4>
        <div className="form-group">
          <input type="datetime-local" name="naechster_termin" value={form.naechster_termin} onChange={handleChange} />
        </div>
      </div>
      )}

      {/* Sitzungsprotokoll */}
      {sessionId && (sessionStatus === 'completed' || sessionStatus === 'in_progress') && (
      <ProtocolSection sessionId={sessionId} sessionNumber={sessionNumber} />
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button className="btn btn-secondary" onClick={handleExport}>
          <FileDown size={16} /> PDF herunterladen
        </button>
        {saved && (
          <span className="save-indicator">
            <Check size={14} /> Gespeichert
          </span>
        )}
        {sessionId && autoSaveStatus !== 'idle' && (
          <span style={{
            fontSize: '0.78rem',
            color: autoSaveStatus === 'error' ? 'var(--danger)' : 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginLeft: 'auto',
          }}>
            {autoSaveStatus === 'saving' && 'Entwurf wird gespeichert...'}
            {autoSaveStatus === 'saved' && <><Check size={12} /> Entwurf gespeichert</>}
            {autoSaveStatus === 'error' && 'Auto-Save fehlgeschlagen'}
          </span>
        )}
      </div>
    </div>
  );
}


// ── Protokoll-Bereich ────────────────────────────────────────

function ProtocolSection({ sessionId, sessionNumber }: { sessionId: string; sessionNumber?: number }) {
  const { showToast } = useToast();
  const [protocol, setProtocol] = useState<any>(null);
  const [content, setContent] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    sessionsApi.getProtocol(sessionId).then(res => {
      if (res.data) {
        setProtocol(res.data);
        setContent(res.data.protocol_content || '');
        setKeyPoints(res.data.key_points || '');
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [sessionId]);

  const handleSave = async () => {
    if (!content.trim()) {
      showToast('Bitte Protokollinhalt eingeben', 'error');
      return;
    }
    setSaving(true);
    try {
      if (protocol?.id) {
        const res = await sessionsApi.updateProtocol(sessionId, protocol.id, { protocol_content: content, key_points: keyPoints || undefined });
        setProtocol(res.data);
        showToast('Protokoll gespeichert', 'success');
      } else {
        const res = await sessionsApi.createProtocol(sessionId, { protocol_content: content, key_points: keyPoints || undefined });
        setProtocol(res.data);
        showToast('Protokoll erstellt', 'success');
      }
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!protocol?.id) {
      showToast('Bitte zuerst das Protokoll speichern', 'error');
      return;
    }
    if (!window.confirm('Protokoll an den Klienten senden?')) return;
    setSending(true);
    try {
      const res = await sessionsApi.sendProtocol(sessionId, protocol.id);
      setProtocol({ ...protocol, client_copy_sent: true, sent_at: res.data.sent_at });
      showToast('Protokoll gesendet', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Senden fehlgeschlagen', 'error');
    } finally {
      setSending(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="block-form-section" style={{ borderTop: '2px solid var(--primary-light)', paddingTop: 16, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Sitzungsprotokoll</h4>
        {protocol?.client_copy_sent && protocol?.sent_at && (
          <span style={{ fontSize: '0.75rem', color: 'var(--success, #2e7d32)', background: 'var(--success-bg, #e8f5e9)', padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>
            Gesendet am {new Date(protocol.sent_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <div className="form-group">
        <label>Protokollinhalt</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Zusammenfassung der Sitzung..."
          rows={6}
          style={{ minHeight: 120 }}
        />
      </div>
      <div className="form-group">
        <label>Kernpunkte (optional)</label>
        <textarea
          value={keyPoints}
          onChange={e => setKeyPoints(e.target.value)}
          placeholder="Die wichtigsten Erkenntnisse und Vereinbarungen..."
          rows={3}
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Speichern...' : (protocol?.id ? 'Aktualisieren' : 'Protokoll erstellen')}
        </button>
        {protocol?.id && (
          <button className="btn btn-secondary btn-sm" onClick={handleSend} disabled={sending || !protocol?.id}>
            <Send size={14} /> {sending ? 'Senden...' : 'An Klienten senden'}
          </button>
        )}
      </div>
    </div>
  );
}
